import { useCallback, useContext, useState } from 'react';
import { Offering, Offerings, Purchases } from '@revenuecat/purchases-js';
import {
  PackageName,
  Terms,
  PaywallPrices,
} from '@components/Paywall/paywall.types';
import { strogging } from '@shd/jslib/infra';
import { getOrdinalSuffix } from '@helpers/utils/formatUtils';
import { UserContext } from '@services/Session';

import useConfigureRevenueCatSDK from '@hooks/monetization/useConfigureRevenueCatSDK';

import { DateTime } from 'luxon';

export const useRevenueCat = () => {
  const packageName = PackageName.PRO;
  const isConfigured = Purchases.isConfigured();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoadingRc, setIsLoadingRc] = useState<boolean>(false);

  const userStore = useContext(UserContext);

  const { initRevenueCat } = useConfigureRevenueCatSDK();

  const setDefaultPurchase = useCallback(async () => {
    // Note: we always want a shdUserId to be set for RevenueCat
    // not having one screws up the entitlements, since that is what we are
    // entitiling against. In a previous iteration we were using a value called
    // 'dk_internal' It is not super clear to me why that was the case but my hunch is we were using it to configure the SDK when the user was logged out.
    if (userStore.user._id) {
      initRevenueCat(userStore.user._id);
    }
  }, [initRevenueCat, userStore]);

  const fetchPrices = useCallback(async () => {
    if (isConfigured) {
      const slhdOfferings: Offerings =
        await Purchases.getSharedInstance().getOfferings();
      const slhdOffering: Offering | null = slhdOfferings.current;

      if (slhdOffering) {
        const monthly =
          slhdOffering.monthly?.webBillingProduct.currentPrice.formattedPrice;
        const annual =
          slhdOffering.annual?.webBillingProduct.currentPrice.formattedPrice;
        return {
          monthly: monthly || '',
          annual: annual || '',
          slhdOfferings,
        };
      } else {
        strogging.log('[useRevenueCat] No offering found');
      }
    } else {
      setDefaultPurchase();
    }
  }, [isConfigured, setDefaultPurchase]);

  const fetchCustomerInfo = useCallback(
    async (prices: PaywallPrices) => {
      if (isConfigured) {
        try {
          return await Purchases.getSharedInstance()
            .getCustomerInfo()
            .then((customerInfo) => {
              const activeEntitlement =
                customerInfo.entitlements.active[packageName];
              if (!activeEntitlement) {
                return {
                  isEntitled: false,
                  activeSubscription: null,
                };
              } else {
                const isPromotional = activeEntitlement.store === 'promotional';
                const productIdentifier = activeEntitlement.productIdentifier;

                let productTerm: { short: string; long: string };
                let productPrice: string;
                let productName: string;

                if (isPromotional) {
                  const formattedExpirationDate =
                    activeEntitlement.expirationDate
                      ? (() => {
                          const dt = DateTime.fromJSDate(
                            activeEntitlement.expirationDate
                          );
                          const day = dt.day;
                          const suffix = getOrdinalSuffix(day);
                          return dt.toFormat(`MMMM d'${suffix}', yyyy`);
                        })()
                      : null;
                  productTerm = {
                    short: formattedExpirationDate
                      ? `Expires ${formattedExpirationDate}`
                      : '',
                    long: formattedExpirationDate
                      ? `Expires ${formattedExpirationDate}`
                      : '',
                  };
                  productPrice = '';
                  productName = PackageName.PROMO_PRO;
                } else {
                  productTerm = productIdentifier.includes('monthly')
                    ? { short: Terms.MONTHLY_SHORT, long: Terms.MONTHLY }
                    : { short: Terms.ANNUAL_SHORT, long: Terms.ANNUAL };
                  productPrice = productIdentifier.includes('monthly')
                    ? prices.monthly
                    : prices.annual;
                  productName = productIdentifier.includes('monthly')
                    ? PackageName.PRO_MONTHLY
                    : PackageName.PRO_ANNUAL;
                }
                return {
                  isEntitled: customerInfo.entitlements.active[packageName]
                    ? true
                    : false,
                  activeSubscription: {
                    productIdentifier,
                    productName,
                    productPrice,
                    productTerm,
                    isPromotional,
                  },
                };
              }
            });
        } catch (e) {
          strogging.log('[useRevenueCat] Error checking entitlement:', e);
          return false;
        }
      } else {
        setDefaultPurchase();
      }
    },
    [packageName, setDefaultPurchase, isConfigured]
  );

  return {
    fetchPrices,
    fetchCustomerInfo,
    packageName,
  };
};
